import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { Subscription } from 'rxjs';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { buildFileMeta } from '../../../upload/upload-meta.util';
import { fileTypeIcon, isImageFile } from '../../../upload/file-type-icon.util';
import { UploadedFileRowComponent } from '../../../upload/uploaded-file-row.component';
import type { ReadDocumentActions, ReadDocumentHolder, ReadDocumentMeta, ReadDocumentRef } from '../readonly.types';

/** Every `upload-document-entry` stores its file in the Uploads module (code `documents`). */
interface UploadDocumentValue extends ReadDocumentHolder {
  entry?: { name?: string; file?: (ReadDocumentMeta & { size?: number }) | null } | null;
}

/** One card: what it shows plus how to open it. */
interface UploadCard {
  key: string;
  ref: ReadDocumentRef;
  name: string;
  meta: string;
  isImage: boolean;
  typeIcon: string;
}

/**
 * Read display for the two upload fields (#21239, task 21243): each file as the Figma "Upload card"
 * — thumbnail for an image, name with an "Geüpload" status, size and upload moment, and one icon
 * button that opens the file (preview for an image, download for anything else). Nothing that
 * changes the answer: no replace, no delete, no accepted-types hint.
 *
 * Where the files come from differs per type, and the wrapper already resolved it
 * (`readonlyPresenceValue`):
 * - `upload-document-entry` — the value IS the referenced Uploads-module entry (its own id and
 *   module, its private/public documents, and `entry.name` / `entry.file.size`);
 * - `upload` — the value is the ENTRY's own document list, so the file lives on the entry being read.
 *
 * Fetching a private thumbnail and opening a file need the API, so both go through
 * `formState.documentActions`, which the page publishes (`DetailsService`). Without it — a story, a
 * standalone form — the card renders without thumbnail or button rather than failing. Object URLs
 * are owned here and revoked when the display goes away.
 */
@Component({
  selector: 'dd-upload-file-display',
  imports: [UploadedFileRowComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
      /* The row's value span truncates on one line; a card is a block that must not be clipped. */
      white-space: normal;
    }
  `,
  template: `
    @for (card of cards(); track card.key) {
      <app-uploaded-file-row
        variant="card"
        state="uploaded"
        [fileName]="card.name"
        [meta]="card.meta"
        [thumbnailUrl]="thumbnails()[card.key] ?? null"
        [fileTypeIcon]="card.isImage ? '' : card.typeIcon"
        [previewable]="!!actions()"
        [previewIcon]="card.isImage ? 'heroicons_outline:eye' : 'heroicons_outline:arrow-down-tray'"
        [previewLabel]="card.isImage ? 'Voorbeeld bekijken' : 'Document downloaden'"
        (preview)="actions()?.open(card.ref)" />
    }
  `,
})
export class UploadFileDisplayComponent extends ReadonlyDisplayBase {
  readonly #destroyRef = inject(DestroyRef);
  readonly #objectUrls = new Map<string, string>();
  readonly #subscriptions = new Map<string, Subscription>();

  protected readonly thumbnails = signal<Record<string, string>>({});

  readonly actions = computed(
    () =>
      (this.field().options?.formState as { documentActions?: ReadDocumentActions } | undefined)?.documentActions ??
      null
  );

  readonly cards = computed<UploadCard[]>(() => {
    const field = this.field();
    const value = this.value();
    return field.type === 'upload' ? this.#entryCards(field, value) : this.#documentEntryCards(value);
  });

  constructor() {
    super();
    // Fetch each image thumbnail once; a card that disappears releases its URL.
    effect(() => {
      const actions = this.actions();
      const images = this.cards().filter(card => card.isImage);
      const currentKeys = new Set(images.map(card => card.key));

      // A card that left the set (removed, value churn; a reused key keeps its thumbnail) no longer needs its subscription
      // or its blob URL — release both before adding anything new.
      for (const key of [...this.#subscriptions.keys()]) {
        if (currentKeys.has(key)) {
          continue;
        }
        this.#subscriptions.get(key)?.unsubscribe();
        this.#subscriptions.delete(key);
        const url = this.#objectUrls.get(key);
        if (url) {
          URL.revokeObjectURL(url);
        }
        this.#objectUrls.delete(key);
        this.thumbnails.update(current => {
          const { [key]: _removed, ...rest } = current;
          return rest;
        });
      }

      if (!actions) {
        return;
      }
      for (const card of images) {
        if (this.#subscriptions.has(card.key)) {
          continue;
        }
        this.#subscriptions.set(
          card.key,
          actions.thumbnail(card.ref).subscribe({
            next: url => {
              if (!url) {
                return;
              }
              this.#objectUrls.set(card.key, url);
              this.thumbnails.update(current => ({ ...current, [card.key]: url }));
            },
            // A thumbnail is a nicety: the card still shows name, meta and the open button.
            error: () => undefined,
          })
        );
      }
    });

    this.#destroyRef.onDestroy(() => {
      this.#subscriptions.forEach(subscription => subscription.unsubscribe());
      this.#objectUrls.forEach(url => URL.revokeObjectURL(url));
    });
  }

  #documentEntryCards(value: unknown): UploadCard[] {
    const document = value as UploadDocumentValue | null | undefined;
    const meta = firstWithId([
      ...(document?.publicDocuments ?? []),
      ...(document?.privateDocuments ?? []),
      document?.entry?.file,
    ]);
    if (!document?.id || !document.moduleId || !meta?.documentId) {
      return [];
    }
    const fileName = meta.documentName || meta.blobReference || '';
    return [
      {
        key: meta.documentId,
        ref: {
          moduleId: document.moduleId,
          entryId: document.id,
          documentId: meta.documentId,
          documentName: document.entry?.name || fileName,
          fileName,
          blobUrl: meta.blobUrl,
        },
        name: document.entry?.name || fileName,
        meta: buildFileMeta({ sizeBytes: document.entry?.file?.size, createdAt: meta.createdDateTime }),
        isImage: isImageFile(fileName),
        typeIcon: fileTypeIcon(fileName),
      },
    ];
  }

  #entryCards(field: FormlyFieldConfig, value: unknown): UploadCard[] {
    const formState = field.options?.formState as { entity?: { id?: string }; entry?: { id?: string } } | undefined;
    const moduleId = formState?.entity?.id;
    const entryId = formState?.entry?.id;
    const documents = Array.isArray(value) ? (value as ReadDocumentMeta[]) : [];
    if (!moduleId || !entryId) {
      return [];
    }
    return documents
      .filter((doc): doc is ReadDocumentMeta & { documentId: string } => !!doc?.documentId)
      .map(doc => {
        const fileName = doc.documentName || doc.blobReference || '';
        return {
          key: doc.documentId,
          ref: {
            moduleId,
            entryId,
            documentId: doc.documentId,
            documentName: fileName,
            fileName,
            blobUrl: doc.blobUrl,
          },
          name: fileName,
          meta: buildFileMeta({ createdAt: doc.createdDateTime }),
          isImage: isImageFile(fileName),
          typeIcon: fileTypeIcon(fileName),
        };
      });
  }
}

function firstWithId(candidates: Array<ReadDocumentMeta | null | undefined>): ReadDocumentMeta | undefined {
  return candidates.find(candidate => !!candidate?.documentId) ?? undefined;
}
